import {formatDate} from "element-ui/src/utils/date-util";
import _ from "lodash";
import PersonServer from "../../../../api/person";
import AthubCloudApi from "@/api/athubCloud";
import {handleError} from "@/util/error-util";
import {Message} from "element-ui";
import BillingServer from "@/api/billing";
import DomainalnApi from "@/api/domainaln";

export function getValue(object, key) {
    let value;
    if (object == null) {
        value = null;
    } else if (key == null || String(key).trim() === '') {
        value = null;
    } else {
        let i = 0, curr = object, items = key.split('.');
        while (i < items.length) {
            if ((curr = curr[items[i]]) == null) {
                break;
            }
            i++;
        }
        value = curr;
    }
    return value;
}

async function dateFormatFn(value) {
    return formatDate(value);
}

export function defaultEquals(cur, pre) {
    let eq;
    if (cur == null && pre == null) {
        eq = true;
    } else if (cur instanceof Number && pre instanceof Number || cur instanceof String && cur instanceof String) {
        eq = cur === pre;
    } else if (cur instanceof Array && pre instanceof Array) {
        eq = arrayEquals(cur, pre);
    } else {
        eq = cur === pre;
    }
    return eq;
}

export function arrayEquals(cur, pre) {
    let eq;
    if (cur.length !== pre.length) {
        eq = false;
    } else {
        eq = true;
        cur.sort();
        pre.sort();
        for (let i = 0, len = cur.length; i < len; i++) {
            if (!defaultEquals(cur[i], pre[i])) {
                eq = false;
                break;
            }
        }
    }
    return eq;
}

export function parse(column, value) {
    let parseValue = value;
    if (column.parse != null && column.parse instanceof Function) {
        parseValue = column.parse(value);
    }
    return parseValue;
}

async function format(column, value) {
    let formatValue = value;
    if (column.format != null && column.format instanceof Function) {
        formatValue = await column.format(value);
    }
    return formatValue;
}

export function getColumnValue(column, object) {
    let value = getValue(object, column.name);
    return parse(column, value);
}

export function columnValueEquals(column, current, previous) {
    let cur = getColumnValue(column, current);
    let pre = getColumnValue(column, previous);
    let equals = defaultEquals;
    if (column.equals != null && column.equals instanceof Function) {
        equals = column.equals;
    }
    return equals(cur, pre);
}

export async function diffColumn(column, current, previous) {
    let operation = null;
    let cur = getColumnValue(column, current);
    let pre = getColumnValue(column, previous);
    let equals = column.equals instanceof Function ? column.equals : defaultEquals;
    if (!equals(cur, pre)) {
        let results = await Promise.all([
            format(column, cur),
            format(column, pre),
        ]);
        operation = {
            label: column.label,
            name: column.name,
            current: results[0],
            previous: results[1],
        };
    }
    return operation;
}

export async function diffColumns(columns, current, previous) {
    let results = [];
    for (let i = 0, len = columns.length; i < len; i++) {
        let item = columns[i];
        let operation = await diffColumn(item, current, previous);
        if (operation != null) {
            results.push(operation);
        }
    }
    return results;
}

export function getContractColumns() {
    return [
        {
            label: '客户名称',
            name: 'customer.name',
        },
        {
            label: '用户名称',
            name: 'enduser.name',
        },
        {
            label: '数据中心',
            name: 'roomIds',
            async format(value) {
                let show = '';
                let ids = value != null && value.trim() !== '' ? value.trim().split(',') : [];
                if (ids.length > 0) {
                    let rooms = await loadRooms();
                    show = getRoomNamesByIds(rooms, ids);
                }
                return show;
            },
        },
        {
            label: '合同名称',
            name: 'name',
        },
        {
            label: '合同编号',
            name: 'sid',
        },
        {
            label: '开始日期',
            name: 'startts',
            format: dateFormatFn,
        },
        {
            label: '结束日期',
            name: 'endts',
            format: dateFormatFn,
        },
        {
            label: '合同类型',
            name: 'contractType',
            async format(value) {
                return value === 'WHOLESALE' ? '批发' : value === 'RETAIL' ? '零售' : value;
            },
        },
        {
            label: '合同机柜数',
            name: 'contractCabinetCount',
        },
        {
            label: '授权人员',
            name: 'userIds',
            async format(value) {
                let show = '';
                let ids = value != null && value.trim() !== '' ? value.trim().split(',') : [];
                if (ids.length > 0) {
                    let persons = await loadPersons();
                    show = getPersonNamesByIds(persons, ids);
                }
                return show;
            }
        },
        {
            label: '备注',
            name: 'comment',
        },
        {
            label: '商机客户名称',
            name: 'businessOpportunities.customerName',
        },
        {
            label: '商机客户编号',
            name: 'businessOpportunities.customerCode',
        },
        {
            label: '销售负责人',
            name: 'businessOpportunities.accountManager',
        },
        {
            label: '商机需求区域',
            name: 'businessOpportunities.salesArea',
        },
        {
            label: '商机规模',
            name: 'businessOpportunities.preSalesScaleComments',
        },
        {
            label: '商机需求时间',
            name: 'businessOpportunities.time',
        },
        {
            label: '商机详细描述',
            name: 'businessOpportunities.comments',
        },
        {
            label: '收款单位',
            name: 'receiptConfig.corporation',
        },
        {
            label: '开户银行',
            name: 'receiptConfig.bank',
        },
        {
            label: '账号',
            name: 'receiptConfig.account',
        },
        {
            label: '联系人',
            name: 'receiptConfig.contact',
        },
        {
            label: '电话',
            name: 'receiptConfig.phone',
        },
    ];
}

export function getContractItemColumns() {
    return [
        {
            label: '计费项',
            name: 'billingConfigItem',
            async format(value) {
                let contractConfigItem = await getContractConfigItemById(value);
                return contractConfigItem == null ? value : contractConfigItem.billingItem;
            }
        },
        {
            label: '计费项名称',
            name: 'name',
        },
        {
            label: '起征点',
            name: 'threshold',
        },
        {
            label: '限额',
            name: 'quota',
        },
        {
            label: '费用/超额费用', // 费用、超额费用
            name: 'rate',
        },
        {
            label: '测试电计费专用条款',
            name: 'ifTest',
            format(value) {
                return value === 0 ? '未勾选' : value === 1 ? '勾选' : value;
            },
        },
        {
            label: '合约功率(kW)',
            name: 'power',
            parse(value) {
                return value == null || value instanceof String && value.trim() === '' ? 0 : parseFloat(value);
            },
        },
        {
            label: '开始日期',
            name: 'beginDate',
            format: dateFormatFn,
        },
        {
            label: '结束日期',
            name: 'endDate',
            format: dateFormatFn,
        },
        {
            label: '当天是否计费',
            name: 'billCSBs',
            equals(current, previous) {
                let result;
                if (current === previous || current == null && previous == null) {
                    result = true;
                } else if (previous == null && current != null) {
                    result = true;
                    for (let i = 0, len = current.length; i < len; i++) {
                        if (current[i].value != null && current[i].value !== '') {
                            result = false;
                            break;
                        }
                    }
                } else if (current == null && previous != null) {
                    result = true;
                    for (let i = 0, len = previous.length; i < len; i++) {
                        if (previous[i].value != null && previous[i].value !== '') {
                            result = false;
                            break;
                        }
                    }
                } else if (current != null && previous != null && current.length === previous.length) {
                    result = true;
                    let len = current.length;
                    current = _.sortBy(current, 'code');
                    previous = _.sortBy(previous, 'code');
                    for (let i = 0; i < len; i++) {
                        if (current[i].code !== previous[i].code || current[i].value !== previous[i].value) {
                            result = false;
                            break;
                        }
                    }
                } else {
                    result = false;
                }
                return result;
            },
            format(billCSBs) {
                let result = '';
                if (billCSBs != null) {
                    for (let i = 0, len = billCSBs.length; i < len; i++) {
                        result += billCSBs[i].name + ': ' + ((billCSBs[i].value == null || billCSBs[i].value === false) ? '未勾选' :
                          billCSBs[i].value === true ? '勾选' : billCSBs[i].value);
                        if (i !== len - 1) {
                            result += ', ';
                        }
                    }
                }
                return result;
            },
        },
    ];
}

export function getContractTermColumns() {
    return [
        {
            label: '账单类型',
            name: 'content.billPeriod',
            async format(value) {
                let billPeriodOpts = await getBillPeriod();
                if(value)
                    return billPeriodOpts.find(item => item.code === value).name
                else
                    return null
            }
        },
        {
            label: '账单开具天',
            name: 'content.billDay',
        },
    ]
}

export async function diffContract(current, previous) {
    let columns = getContractColumns();
    let cur = current.contract;
    let pre = previous.contract;
    return diffColumns(columns, cur, pre);
}

export async function diffContractItem(current, previous) {
    let columns = getContractItemColumns();
    return diffColumns(columns, current, previous);
}

export async function diffContractTerm(current, previous) {
    let columns = getContractTermColumns();
    return diffColumns(columns, current, previous);
}

export async function diffContractItems(current, previous) {
    let curs = current.contractItems;
    curs = curs == null ? [] : curs;
    let pres = previous.contractItems;
    pres = pres == null ? [] : pres;
    let curMap = _.keyBy(curs, 'uuid');
    let preMap = _.keyBy(pres, 'uuid');
    console.log('diffContractItems', curMap, preMap)

    let configItemIds = curs.map(e => e.billingConfigItem).filter(e => e != null);
    configItemIds.concat(pres.map(e => e.billingConfigItem).filter(e => e != null));
    await loadContractConfigItemsByIds(configItemIds);
    let billCSBs = await getBillCSBs();
    setContractItemsBillCSBs(curs, billCSBs);
    setContractItemsBillCSBs(pres, billCSBs);

    let adds = [], updates = [], deletes = [];
    for (let i = 0, len = curs.length; i < len; i++) {
        let cur = curs[i];
        let pre = preMap[cur.uuid];
        if (pre == null) {
            // add
            adds.push(cur);
        } else if (cur.release == null || cur.release === 2) {
            // update
            let changes = await diffContractItem(cur, pre);
            if (changes != null && changes.length > 0) {
                updates.push({
                    data: cur,
                    changes: changes,
                });
            }
        } else if (cur.release === 3) {
            deletes.push(cur);
        }
    }
    for (let i = 0, len = pres.length; i < len; i++) {
        let pre = pres[i];
        let cur = curMap[pre.uuid];
        if (cur == null) {
            // delete
            deletes.push(pre);
        }
    }
    return {
        adds: adds,
        updates: updates,
        deletes: deletes,
    };
}

export async function diffContractTerms(current, previous) {
    let curs = current.contractTerms;
    curs = curs == null ? [] : curs;
    let pres = previous.contractTerms;

    pres = pres == null ? [] : pres;
    curs = curs.map(c => ({...c, content: c.content ? JSON.parse(c.content) : null }))
    pres = pres.map(c => ({...c, content: c.content ? JSON.parse(c.content) : null }))
    console.log(curs, pres)
    let curMap = _.keyBy(curs, 'uuid');
    let preMap = _.keyBy(pres, 'uuid');
    console.log(curMap, preMap)

    let adds = [], updates = [], deletes = [];
    for (let i = 0, len = curs.length; i < len; i++) {
        let cur = curs[i];
        let pre = preMap[cur.uuid];
        if (pre == null) {
            // add
            adds.push(cur);
        } else if (cur.release == null || cur.release === 2) {
            // update
            // debugger
            let changes = await diffContractTerm(cur, pre);
            if (changes != null && changes.length > 0) {
                updates.push({
                    data: cur,
                    changes: changes,
                });
            }
        } else if (cur.release === 3) {
            deletes.push(cur);
        }
    }
    for (let i = 0, len = pres.length; i < len; i++) {
        let pre = pres[i];
        let cur = curMap[pre.uuid];
        if (cur == null) {
            // delete
            deletes.push(pre);
        }
    }
    console.log({
        adds: adds,
        updates: updates,
        deletes: deletes,
    })
    return {
        adds: adds,
        updates: updates,
        deletes: deletes,
    };
}

export async function differentiate(current, previous) {
    let type;
    let contractChanges = [];
    let contractItemsChanges = {
        adds: [],
        updates: [],
        deletes: [],
    };
    let contractTermsChanges = {
        adds: [],
        updates: [],
        deletes: [],
    };
    if (current == null) {
        type = 0; // 当前值为null，不能比较
    } else if (previous == null) {
        type = 1; // 前一条为null，不需比较，说明当前是新增
    } else {
        if (current.valueObject == null) {
            current.valueObject = JSON.parse(current.value);
        }
        if (previous.valueObject == null) {
            previous.valueObject = JSON.parse(previous.value);
        }

        type = 2; // 比较修改内容
        contractChanges = await diffContract(current.valueObject, previous.valueObject);
        contractItemsChanges = await diffContractItems(current.valueObject, previous.valueObject);
        contractTermsChanges = await diffContractTerms(current.valueObject, previous.valueObject);
    }

    console.log({
        type: type,
        contract: contractChanges,
        contractItems: contractItemsChanges,
        contractTerms: contractTermsChanges
    })
    return {
        type: type,
        contract: contractChanges,
        contractItems: contractItemsChanges,
        contractTerms: contractTermsChanges
    };
}

let _persons = null;
async function loadPersons() {
    let persons = _persons;
    if (persons == null) {
        persons = await PersonServer.personApi.fetchByRedis({});
        _persons = persons;
    }
    return persons;
}

export function getPersonNamesByIds(persons, ids) {
    let names;
    if (ids == null || ids.length <= 0) {
        names = [];
    } else {
        let personsMap = _.keyBy(persons, 'id');
        names = [];
        for (let i = 0, len = ids.length; i < len; i++) {
            let person = personsMap[ids[i]];
            if (person == null) {
                names.push(ids[i]);
            } else {
                names.push(person.realName);
            }
        }
    }
    return names.join(', ');
}

export async function getPersonsByIds(ids) {
    let persons = [];
    if (ids != null && ids.length > 0) {
        let response;
        try {
            response = await PersonServer.personApi.list({personuids: ids});
            if (String(response.data.code) === '200') {
                persons = response.data.data;
            } else {
                Message.error(response.data.message);
            }
        } catch (e) {
            handleError(e);
        }
    }
    return persons;
}

let _rooms = null;
async function loadRooms() {
    let rooms = _rooms;
    if (rooms == null) {
        rooms = await AthubCloudApi.fetchAllSysroomList();
        _rooms = rooms;
    }
    return rooms;
}

export function getRoomNamesByIds(rooms, ids) {
    let names = [];
    if (ids != null && ids.length > 0) {
        let roomsMap = _.keyBy(rooms, 'id');
        names = [];
        for (let i = 0, len = ids.length; i < len; i++) {
            let room = roomsMap[ids[i]];
            if (room == null) {
                names.push(ids[i]);
            } else {
                names.push(room.name);
            }
        }
    }
    return names.join(', ');
}

let _contractConfigItems = null;

let _contractConfigItemsMap = null;

function getContractConfigItemsMapKey(id) {
    return 'id_' + id;
}

function addContractConfigItemToCache(contractConfigItem) {
    if (contractConfigItem != null) {
        let key = getContractConfigItemsMapKey(contractConfigItem.id);
        if (_contractConfigItemsMap[key] == null) {
            _contractConfigItems.push(contractConfigItem);
            _contractConfigItemsMap[key] = contractConfigItem;
        }
    }
}

export async function getContractConfigItemById(configItemId) {
    let contractConfigItem = null;
    if (configItemId != null) {
        if (_contractConfigItemsMap == null || (contractConfigItem = _contractConfigItemsMap[getContractConfigItemsMapKey(configItemId)]) == null) {
            let response = await BillingServer.contract.getContractConfigItemByContractConfigItemId(configItemId);
            if (response.data.success) {
                contractConfigItem = response.data.data;

                addContractConfigItemToCache(contractConfigItem);
            }
        }
    }
    return contractConfigItem;
}

export async function loadContractConfigItemsByIds(configItemIds) {
    let contractConfigItems = [];
    if (configItemIds != null && configItemIds.length > 0) {
        let response = await BillingServer.contract.getContractConfigItems(configItemIds);
        if (response.data.success) {
            contractConfigItems = response.data.data;
        }
        _contractConfigItems = contractConfigItems;
        _contractConfigItemsMap = _.keyBy(_contractConfigItems, e => getContractConfigItemsMapKey(e.id));
    }
    return contractConfigItems;
}

export async function getBillCSBs() {
    let billCSBs = [];
    let items = await DomainalnApi.list({domainid: 'BILLCSB'});
    for (let i = 0, len = items.length; i < len; i++) {
        let item = items[i];
        billCSBs.push({
            code: item.value,
            name: item.description,
        });
    }
    return billCSBs;
}

export async function getBillPeriod() {
    let opts = [];
    let items = await DomainalnApi.list({domainid: 'BILLPERIOD'});
    for (let i = 0, len = items.length; i < len; i++) {
        let item = items[i];
        opts.push({
            code: item.value,
            name: item.description,
        });
    }
    return opts;
}

export function setContractItemBillCSBs(contractItem, billCSBs) {
    billCSBs = billCSBs == null ? [] : JSON.parse(JSON.stringify(billCSBs));
    if (contractItem.attributes != null && contractItem.attributes !== '') {
        let attributes = JSON.parse(contractItem.attributes);
        let savedBillCSBs = attributes.billCSBs;
        savedBillCSBs = savedBillCSBs == null ? [] : savedBillCSBs;
        let savedBillCSBsMap = _.keyBy(savedBillCSBs, 'code');
        for (let i = 0, len = billCSBs.length; i < len; i++) {
            billCSBs[i].value = savedBillCSBsMap[billCSBs[i].code] == null ? false : savedBillCSBsMap[billCSBs[i].code].value;
        }
    }
    for (let i = 0, len = billCSBs.length; i < len; i++) {
        billCSBs[i].value = !!billCSBs[i].value;
    }
    contractItem.billCSBs = billCSBs;
    return contractItem;
}

function setContractItemsBillCSBs(contractItems, billCSBs) {
    for (let i = 0, len = contractItems.length; i < len; i++) {
        setContractItemBillCSBs(contractItems[i], billCSBs);
    }
}
